import { useState, useEffect, KeyboardEvent } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Avatar,
  CircularProgress,
  makeStyles,
  List,
  ListItem,
  ListItemText,
  Chip,
  Collapse,
  FormControlLabel,
  Checkbox,
  Tooltip,
} from '@material-ui/core';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import StarIcon from '@material-ui/icons/Star';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import { WhatsNewRelease, MajorReleaseGroup } from './githubReleasesService';
import idpLogo from '../Root/logo/idp-logo.svg';
import ReactMarkdown from 'react-markdown';

// Short-viewport (e.g. landscape phones ~390px tall): let the whole main section
// scroll so the notes and dismissal action stay reachable instead of being clipped.
const SHORT_SCREEN_QUERY = '@media (max-height:700px)';

const useStyles = makeStyles(theme => ({
  dialog: {
    '& .MuiDialog-paper': {
      borderRadius: 16,
      overflow: 'hidden',
      maxWidth: 960,
      width: '100%',
      [theme.breakpoints.down('sm')]: {
        width: 'calc(100vw - 32px)',
        maxWidth: 'none',
        margin: theme.spacing(2),
      },
    },
  },
  dialogContent: {
    display: 'flex',
    padding: 0,
    height: 640,
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      height: 'calc(100vh - 96px)',
    },
    [SHORT_SCREEN_QUERY]: {
      height: 'auto',
      maxHeight: 'calc(100vh - 64px)',
    },
  },
  sidebar: {
    width: 260,
    minWidth: 260,
    borderRight: `1px solid ${theme.palette.divider}`,
    backgroundColor:
      theme.palette.type === 'dark'
        ? theme.palette.background.default
        : theme.palette.grey[50],
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      minWidth: 0,
      height: 156,
      flex: '0 0 156px',
      borderRight: 'none',
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
  },
  sidebarHeader: {
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
  },
  sidebarTitle: {
    fontWeight: 600,
    fontSize: '0.9rem',
    color: theme.palette.text.primary,
  },
  releaseList: {
    flex: 1,
    overflow: 'auto',
    padding: theme.spacing(1),
  },
  majorGroupHeader: {
    borderRadius: 8,
    marginBottom: theme.spacing(0.25),
    padding: theme.spacing(1, 1.5),
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  majorGroupHeaderSelected: {
    backgroundColor: theme.palette.action.selected,
    '&:hover': {
      backgroundColor: theme.palette.action.selected,
    },
  },
  majorGroupLabel: {
    fontWeight: 700,
    fontSize: '0.9rem',
    color: theme.palette.text.primary,
  },
  majorGroupDate: {
    fontSize: '0.7rem',
    color: theme.palette.text.secondary,
  },
  majorGroupMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  releaseItem: {
    borderRadius: 6,
    marginBottom: theme.spacing(0.25),
    paddingLeft: theme.spacing(3),
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  releaseItemSelected: {
    backgroundColor: theme.palette.action.selected,
    '&:hover': {
      backgroundColor: theme.palette.action.selected,
    },
  },
  releaseItemContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  releaseVersion: {
    fontWeight: 500,
    fontSize: '0.8rem',
  },
  releaseDate: {
    fontSize: '0.7rem',
    color: theme.palette.text.secondary,
  },
  latestChip: {
    height: 18,
    fontSize: '0.6rem',
    fontWeight: 600,
    backgroundColor: theme.palette.success.main,
    color: theme.palette.success.contrastText,
  },
  minorCount: {
    fontSize: '0.65rem',
    color: theme.palette.text.secondary,
    fontWeight: 400,
  },
  mainContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    minHeight: 0,
    [SHORT_SCREEN_QUERY]: {
      overflow: 'auto',
    },
  },
  header: {
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    color: theme.palette.primary.contrastText,
    padding: theme.spacing(3),
    paddingBottom: theme.spacing(2),
    textAlign: 'center',
    position: 'relative',
    overflow: 'visible',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
    },
    [SHORT_SCREEN_QUERY]: {
      flexShrink: 0,
    },
  },
  headerPattern: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    opacity: 0.1,
    background: `radial-gradient(circle at 20% 50%, ${theme.palette.primary.contrastText} 1px, transparent 1px), radial-gradient(circle at 80% 50%, ${theme.palette.primary.contrastText} 1px, transparent 1px)`,
    backgroundSize: '40px 40px',
  },
  headerContent: {
    position: 'relative',
    zIndex: 1,
  },
  headerLogo: {
    height: 30,
    width: 'auto',
    display: 'block',
    margin: `0 auto ${theme.spacing(1)}px`,
  },
  versionBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    backgroundColor:
      theme.palette.type === 'dark'
        ? 'rgba(255,255,255,0.15)'
        : 'rgba(255,255,255,0.25)',
    color: theme.palette.primary.contrastText,
    padding: theme.spacing(0.5, 2),
    borderRadius: 20,
    fontWeight: 700,
    fontSize: '1rem',
    marginTop: theme.spacing(1),
  },
  title: {
    fontWeight: 700,
    fontSize: '1.25rem',
    marginBottom: theme.spacing(0.5),
  },
  subtitle: {
    opacity: 0.95,
    fontSize: '0.85rem',
    color: theme.palette.primary.contrastText,
  },
  content: {
    flex: 1,
    padding: theme.spacing(2.5),
    overflow: 'auto',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
    },
    [SHORT_SCREEN_QUERY]: {
      flex: 'none',
      overflow: 'visible',
    },
  },
  description: {
    fontSize: '0.95rem',
    lineHeight: 1.7,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(2),
    textAlign: 'center',
  },
  markdownSection: {
    backgroundColor: theme.palette.action.hover,
    borderRadius: 12,
    padding: theme.spacing(2.5),
    marginBottom: theme.spacing(2),
    '& h1, & h2, & h3': {
      color: theme.palette.primary.main,
      fontWeight: 600,
      marginTop: theme.spacing(2.5),
      marginBottom: theme.spacing(1.5),
      '&:first-child': {
        marginTop: 0,
      },
    },
    '& h1': {
      fontSize: '1.3rem',
    },
    '& h2': {
      fontSize: '1.15rem',
    },
    '& h3': {
      fontSize: '1rem',
    },
    '& ul, & ol': {
      paddingLeft: theme.spacing(2.5),
      margin: theme.spacing(1, 0),
      listStyleType: 'none',
    },
    '& ul > li': {
      position: 'relative',
      marginBottom: theme.spacing(1.25),
      paddingLeft: theme.spacing(3),
      lineHeight: 1.6,
      color: theme.palette.text.primary,
      fontSize: '0.95rem',
      fontWeight: 500,
      '&:before': {
        content: '"✓"',
        position: 'absolute',
        left: 0,
        top: '1px',
        color: theme.palette.success.main,
        fontWeight: 700,
        fontSize: '1rem',
      },
      // Nested lists (sub-bullets)
      '& ul': {
        marginTop: theme.spacing(1),
        paddingLeft: theme.spacing(2),
      },
      '& ul > li': {
        fontSize: '0.875rem',
        fontWeight: 400,
        color: theme.palette.text.primary,
        marginBottom: theme.spacing(0.75),
        paddingLeft: theme.spacing(2.5),
        '&:before': {
          content: '"•"',
          color: theme.palette.primary.main,
          fontSize: '0.9rem',
          top: '0px',
        },
        // Third level nesting
        '& ul > li': {
          fontSize: '0.85rem',
          paddingLeft: theme.spacing(2),
          '&:before': {
            content: '"◦"',
            fontSize: '0.8rem',
          },
        },
      },
    },
    '& ol > li': {
      marginBottom: theme.spacing(1),
      paddingLeft: theme.spacing(1),
      lineHeight: 1.6,
      color: theme.palette.text.primary,
      fontSize: '0.95rem',
    },
    '& p': {
      marginBottom: theme.spacing(1.5),
      lineHeight: 1.7,
      color: theme.palette.text.primary,
      '&:last-child': {
        marginBottom: 0,
      },
    },
    '& code': {
      backgroundColor:
        theme.palette.type === 'dark'
          ? 'rgba(255, 255, 255, 0.1)'
          : 'rgba(0, 0, 0, 0.06)',
      color: theme.palette.type === 'dark' ? '#ff79c6' : '#c7254e',
      padding: theme.spacing(0.25, 0.75),
      borderRadius: 4,
      fontSize: '0.875em',
      fontFamily: 'monospace',
    },
    '& pre': {
      backgroundColor:
        theme.palette.type === 'dark'
          ? 'rgba(0, 0, 0, 0.3)'
          : 'rgba(0, 0, 0, 0.04)',
      padding: theme.spacing(2),
      borderRadius: 8,
      overflow: 'auto',
      marginBottom: theme.spacing(2),
      '& code': {
        backgroundColor: 'transparent',
        padding: 0,
        color: 'inherit',
      },
    },
    '& a': {
      color: theme.palette.primary.main,
      textDecoration: 'none',
      fontWeight: 500,
      '&:hover': {
        textDecoration: 'underline',
      },
    },
    '& strong': {
      fontWeight: 600,
      color: theme.palette.text.primary,
    },
    '& blockquote': {
      borderLeft: `3px solid ${theme.palette.primary.main}`,
      paddingLeft: theme.spacing(2),
      margin: theme.spacing(2, 0),
      fontStyle: 'italic',
      color: theme.palette.text.primary,
    },
  },
  markdownTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    fontWeight: 600,
    marginBottom: theme.spacing(2),
    color: theme.palette.primary.main,
  },
  authorSection: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
    marginTop: theme.spacing(2),
  },
  authorAvatar: {
    width: 28,
    height: 28,
  },
  authorText: {
    fontSize: '0.85rem',
    color: theme.palette.text.secondary,
  },
  actions: {
    padding: theme.spacing(1.5, 3),
    justifyContent: 'space-between',
    borderTop: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: theme.spacing(1),
      // Column layout: drop MUI's default left margin between stacked controls.
      '& > :not(:first-child)': {
        marginLeft: 0,
      },
    },
    [SHORT_SCREEN_QUERY]: {
      flexShrink: 0,
    },
  },
  actionsRight: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'stretch',
      width: '100%',
    },
  },
  primaryButton: {
    borderRadius: 25,
    padding: theme.spacing(1, 4),
    fontWeight: 600,
    textTransform: 'none',
    fontSize: '1rem',
  },
  secondaryButton: {
    borderRadius: 25,
    padding: theme.spacing(1, 3),
    textTransform: 'none',
  },
  autoPopupCheckbox: {
    marginLeft: theme.spacing(0.5),
    '& .MuiTypography-root': {
      fontSize: '0.8rem',
      color: theme.palette.text.secondary,
    },
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(6),
    gap: theme.spacing(2),
  },
  errorContainer: {
    textAlign: 'center',
    padding: theme.spacing(4),
  },
  headerReleaseDate: {
    fontSize: '0.75rem',
    opacity: 0.9,
    marginTop: theme.spacing(0.5),
    color: theme.palette.primary.contrastText,
  },
  showAllReleases: {
    padding: theme.spacing(1.5, 2),
    borderTop: `1px solid ${theme.palette.divider}`,
    textAlign: 'center',
  },
  showAllReleasesButton: {
    textTransform: 'none',
    fontSize: '0.8rem',
    fontWeight: 500,
    borderRadius: 20,
    width: '100%',
  },
}));

type Props = {
  open: boolean;
  majorGroups: MajorReleaseGroup[];
  latestRelease: WhatsNewRelease | null;
  loading: boolean;
  error: string | null;
  hasNewVersion: boolean;
  autoPopupDisabled: boolean;
  onClose: (markAsRead: boolean) => void;
  onRefresh: () => void;
  onAutoPopupChange: (disabled: boolean) => void;
};

const formatDate = (date: Date): string => {
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(date);
};

const formatShortDate = (date: Date): string => {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
};

export const WhatsNewDialog = ({
  open,
  majorGroups,
  latestRelease,
  loading,
  error,
  hasNewVersion,
  autoPopupDisabled,
  onClose,
  onRefresh,
  onAutoPopupChange,
}: Props) => {
  const classes = useStyles();
  const [selectedRelease, setSelectedRelease] =
    useState<WhatsNewRelease | null>(null);
  // Track which major groups are expanded in the sidebar
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Set selected release and expand the latest major group when dialog opens
  useEffect(() => {
    if (open && latestRelease && !selectedRelease) {
      setSelectedRelease(latestRelease);
      // Auto-expand the major group containing the latest release
      if (majorGroups.length > 0) {
        setExpandedGroups(new Set([majorGroups[0].majorVersion]));
      }
    }
  }, [open, latestRelease, selectedRelease, majorGroups]);

  // Reset when dialog closes
  useEffect(() => {
    if (!open) {
      setSelectedRelease(null);
      setExpandedGroups(new Set());
    }
  }, [open]);

  const handleGroupClick = (group: MajorReleaseGroup) => {
    const isExpanded = expandedGroups.has(group.majorVersion);
    const nextExpandedGroups = new Set(expandedGroups);

    if (isExpanded) {
      // Collapse the group
      nextExpandedGroups.delete(group.majorVersion);
    } else {
      // Expand the group and select its latest release
      nextExpandedGroups.add(group.majorVersion);
    }

    setExpandedGroups(nextExpandedGroups);

    if (!isExpanded) {
      setSelectedRelease(group.latestRelease);
    }
  };

  const currentRelease = selectedRelease || latestRelease;
  const isLatest = currentRelease?.id === latestRelease?.id;

  if (!open) return null;

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      className={classes.dialog}
      onClose={() => onClose(false)}
    >
      {loading ? (
        <Box className={classes.loadingContainer}>
          <CircularProgress />
          <Typography color="textSecondary">Loading...</Typography>
        </Box>
      ) : error ? (
        <Box className={classes.errorContainer}>
          <Typography variant="h6" gutterBottom color="error">
            Failed to load
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            {error}
          </Typography>
          <Box style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
            <Button onClick={onRefresh} color="primary" variant="outlined">
              Try Again
            </Button>
            <Button onClick={() => onClose(false)} color="default">
              Close
            </Button>
          </Box>
        </Box>
      ) : majorGroups.length > 0 && currentRelease ? (
        <Box className={classes.dialogContent}>
          {/* Sidebar with major/minor release hierarchy */}
          <Box className={classes.sidebar}>
            <Box className={classes.sidebarHeader}>
              <Typography className={classes.sidebarTitle}>Releases</Typography>
            </Box>
            <List className={classes.releaseList} disablePadding>
              {majorGroups.map((group, groupIndex) => {
                const isGroupExpanded = expandedGroups.has(group.majorVersion);
                const isGroupActive = group.releases.some(
                  r => r.id === currentRelease.id,
                );

                return (
                  <Box key={group.majorVersion}>
                    {/* Major version header */}
                    <Box
                      className={`${classes.majorGroupHeader} ${
                        isGroupActive && !isGroupExpanded
                          ? classes.majorGroupHeaderSelected
                          : ''
                      }`}
                      role="button"
                      tabIndex={0}
                      aria-expanded={isGroupExpanded}
                      onClick={() => handleGroupClick(group)}
                      onKeyDown={(e: KeyboardEvent) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleGroupClick(group);
                        }
                      }}
                    >
                      <Box>
                        <Box
                          display="flex"
                          alignItems="center"
                          style={{ gap: 6 }}
                        >
                          <Typography className={classes.majorGroupLabel}>
                            v{group.label}
                          </Typography>
                          {groupIndex === 0 && (
                            <Chip
                              label="Latest"
                              size="small"
                              className={classes.latestChip}
                            />
                          )}
                        </Box>
                        <Typography className={classes.majorGroupDate}>
                          {formatShortDate(group.lastReleasedAt)}
                        </Typography>
                      </Box>
                      <Box className={classes.majorGroupMeta}>
                        <Typography className={classes.minorCount}>
                          {group.releases.length} release
                          {group.releases.length !== 1 ? 's' : ''}
                        </Typography>
                        {isGroupExpanded ? (
                          <ExpandLessIcon fontSize="small" color="action" />
                        ) : (
                          <ExpandMoreIcon fontSize="small" color="action" />
                        )}
                      </Box>
                    </Box>

                    {/* Minor releases within this major group */}
                    <Collapse in={isGroupExpanded}>
                      <List disablePadding>
                        {group.releases.map(release => (
                          <ListItem
                            key={release.id}
                            button
                            className={`${classes.releaseItem} ${
                              release.id === currentRelease.id
                                ? classes.releaseItemSelected
                                : ''
                            }`}
                            onClick={() => setSelectedRelease(release)}
                          >
                            <ListItemText
                              disableTypography
                              primary={
                                <Box className={classes.releaseItemContent}>
                                  <Box>
                                    <Typography
                                      className={classes.releaseVersion}
                                    >
                                      v{release.version}
                                    </Typography>
                                    <Typography className={classes.releaseDate}>
                                      {formatShortDate(release.publishedAt)}
                                    </Typography>
                                  </Box>
                                </Box>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Collapse>
                  </Box>
                );
              })}
            </List>

            {/* Link to full releases page on GitHub */}
            <Box className={classes.showAllReleases}>
              <Button
                component="a"
                href={(() => {
                  const url = majorGroups[0]?.latestRelease?.url || '';
                  const tagIndex = url.indexOf('/releases/');
                  return tagIndex !== -1
                    ? `${url.substring(0, tagIndex)}/tags`
                    : 'https://github.com/bayer-int/backstage/tags';
                })()}
                target="_blank"
                rel="noopener noreferrer"
                size="small"
                color="primary"
                className={classes.showAllReleasesButton}
                endIcon={<OpenInNewIcon style={{ fontSize: 14 }} />}
              >
                Show all releases
              </Button>
            </Box>
          </Box>

          {/* Main content area */}
          <Box className={classes.mainContent}>
            {/* Header */}
            <Box className={classes.header}>
              <Box className={classes.headerPattern} />
              <Box className={classes.headerContent}>
                    <img
                      className={classes.headerLogo}
                      src={idpLogo}
                      alt="IDP Engineers Hub"
                    />
                <Typography className={classes.title}>
                  {hasNewVersion && isLatest
                    ? '🎉 New Update Available!'
                    : "What's New"}
                </Typography>
                <Typography className={classes.subtitle}>
                  {currentRelease.title}
                </Typography>
                <Box className={classes.versionBadge}>
                  <StarIcon fontSize="small" />v{currentRelease.version}
                </Box>
                <Typography className={classes.headerReleaseDate}>
                  Released on {formatDate(currentRelease.publishedAt)}
                </Typography>
              </Box>
            </Box>

            {/* Content */}
            <DialogContent className={classes.content}>
              <Typography className={classes.description}>
                {currentRelease.description}
              </Typography>

              {currentRelease.body && currentRelease.body.trim() ? (
                <Box className={classes.markdownSection}>
                  <Typography className={classes.markdownTitle}>
                    <StarIcon fontSize="small" />
                    What's Included
                  </Typography>
                  <ReactMarkdown
                    skipHtml
                    disallowedElements={['script', 'iframe', 'object', 'embed']}
                  >
                    {currentRelease.body}
                  </ReactMarkdown>
                </Box>
              ) : (
                <Typography
                  color="textSecondary"
                  style={{ textAlign: 'center', padding: 16 }}
                >
                  No release notes available
                </Typography>
              )}

              {currentRelease.author.name && (
                <Box className={classes.authorSection}>
                  <Avatar
                    src={currentRelease.author.avatarUrl}
                    className={classes.authorAvatar}
                  >
                    {currentRelease.author.name.charAt(0).toUpperCase()}
                  </Avatar>
                  <Typography className={classes.authorText}>
                    Released by <strong>{currentRelease.author.name}</strong>
                  </Typography>
                </Box>
              )}
            </DialogContent>

            {/* Actions */}
            <DialogActions className={classes.actions}>
              <Tooltip title="When unchecked, the popup will not appear automatically on major releases. You can still open it manually.">
                <FormControlLabel
                  className={classes.autoPopupCheckbox}
                  control={
                    <Checkbox
                      size="small"
                      checked={!autoPopupDisabled}
                      onChange={e => onAutoPopupChange(!e.target.checked)}
                      color="default"
                    />
                  }
                  label="Show automatically for major releases"
                />
              </Tooltip>
              <Box className={classes.actionsRight}>
                <Button
                  component="a"
                  href={currentRelease.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  color="primary"
                  className={classes.secondaryButton}
                  endIcon={<OpenInNewIcon fontSize="small" />}
                  style={{ textDecoration: 'none' }}
                >
                  View Details
                </Button>
                <Button
                  onClick={() => onClose(true)}
                  color="primary"
                  variant="contained"
                  className={classes.primaryButton}
                >
                  {hasNewVersion ? 'Got it!' : 'Close'}
                </Button>
              </Box>
            </DialogActions>
          </Box>
        </Box>
      ) : (
        <Box className={classes.loadingContainer}>
          <Typography color="textSecondary">No releases found</Typography>
          <Button onClick={() => onClose(false)} color="primary">
            Close
          </Button>
        </Box>
      )}
    </Dialog>
  );
};
